import {
  Reservation,
  RecentZoneResponse,
  ReservationContractSignupSaveRequest,
  Task,
  OpenTicketResponse,
} from '@parkingbase/ecommerce-api-dev';
import { ReservationService } from '@/services/reservation.service';

import { create } from 'zustand';
import { downloadBlob } from '@/lib/file-download';
import { logger } from '@/lib/logger';
import { extractApiErrorMessage } from '@/lib/api-error-utils';

const reservationService = new ReservationService();

/**
 * Lifecycle of the customer reservation-list load (PB-9495). Kept separate from
 * `isLoading`, which `getAllPendingDisclaimertasks` also toggles, so a fast
 * pending-tasks call can never make a slow reservations load read as finished.
 */
export type ReservationsLoadStatus = 'idle' | 'loading' | 'success' | 'error';

/** Options for {@link ReservationState.getAllReservationsByUser}. */
export interface GetAllReservationsByUserOptions {
  /**
   * Reuse a request that is already in flight instead of starting a new one.
   * Only mount-time readers opt in; a caller that just changed reservation data
   * must not, because the shared request may predate its write.
   */
  shareInFlight?: boolean;
}

// Module-level so the latest-wins check is a synchronous read, never a React
// state snapshot that a stale closure could hold.
let reservationsInFlight: Promise<void> | null = null;
let reservationsRequestSeq = 0;

interface ReservationState {
  currentReservations: Reservation[];
  pastReservations: Reservation[];
  recentZones: RecentZoneResponse[];
  pendingTasks: Task[];
  openTicketResponse: OpenTicketResponse | null;
  isLoading: boolean;
  /** Status of the latest reservation-list load (PB-9495). */
  reservationsStatus: ReservationsLoadStatus;
  /** Backend error from the most recent signContract failure, or null (PB-1712, additive). */
  error: string | null;
  clearError: () => void;
  /**
   * Loads the signed-in customer's current and past reservations.
   *
   * ## Invariants:
   * - A call with `shareInFlight: true` reuses the request already in flight
   *   (AppDataProvider, useActiveSessionTimer and the Reservations page all fire
   *   on the same page load, and the endpoint is expensive).
   * - A call without it always starts a new request, as before PB-9495: callers
   *   that just wrote reservation data (cancel, vehicle update, post-booking
   *   lookup) and the user's retry rely on that, because a shared request may
   *   have started before their write.
   * - Only the latest-started request writes state; an older response that lands
   *   later is dropped. `reset()` invalidates any in-flight request, so a response
   *   that arrives after logout cannot repopulate the previous user's data.
   * - Never rejects. A failure is logged and recorded as
   *   `reservationsStatus: 'error'`; the existing lists are kept, so the UI can
   *   tell a failed load from a genuinely empty one.
   */
  getAllReservationsByUser: (options?: GetAllReservationsByUserOptions) => Promise<void>;
  reset: () => void;
  fetchRecentZones: () => Promise<void>;
  fetchOpenTicketDetails: () => Promise<void>;
  getAllPendingDisclaimertasks: () => Promise<void>;
  cancelReservation: (reservationId: string, wholeGroup?: boolean) => Promise<boolean>;
  downloadReceipt: (company: string, reservationId: string) => Promise<void>;
  signContract: (
    companyId: string,
    taskId: string,
    payload: ReservationContractSignupSaveRequest
  ) => Promise<boolean>;
}

export const useReservationStore = create<ReservationState>((set, get) => ({
  currentReservations: [],
  pastReservations: [],
  recentZones: [],
  pendingTasks: [],
  openTicketResponse: null,
  isLoading: false,
  reservationsStatus: 'idle',
  error: null,
  clearError: () => set({ error: null }),
  reset: () => {
    // Invalidate any in-flight reservation load so its response is dropped.
    reservationsRequestSeq += 1;
    reservationsInFlight = null;
    set({
      currentReservations: [],
      pastReservations: [],
      recentZones: [],
      pendingTasks: [],
      openTicketResponse: null,
      isLoading: false,
      reservationsStatus: 'idle',
    });
  },
  getAllReservationsByUser: (options?: GetAllReservationsByUserOptions) => {
    if (options?.shareInFlight && reservationsInFlight) {
      return reservationsInFlight;
    }
    reservationsRequestSeq += 1;
    const seq = reservationsRequestSeq;
    const isLatest = () => seq === reservationsRequestSeq;
    let settled = false;
    set({ isLoading: true, reservationsStatus: 'loading' });
    const request = (async () => {
      try {
        const reservations = await reservationService.getReservationsByUser();
        if (isLatest()) {
          set({
            pastReservations: reservations.pastReservation || [],
            currentReservations: reservations.currentReservation || [],
            reservationsStatus: 'success',
          });
        }
      } catch (error) {
        logger.error('Failed to fetch reservations for user', error, {
          feature: 'reservation-store',
          route: 'getAllReservationsByUser',
        });
        if (isLatest()) {
          set({ reservationsStatus: 'error' });
        }
      } finally {
        settled = true;
        if (isLatest()) {
          set({ isLoading: false });
          reservationsInFlight = null;
        }
      }
    })();
    // A request that settled synchronously must not be left as "in flight",
    // or every later call would share its already-finished result.
    if (!settled) {
      reservationsInFlight = request;
    }
    return request;
  },
  fetchOpenTicketDetails: async () => {
    try {
      const data = await reservationService.getOpenTicketDetailsFromWhiteLabel();
      set({ openTicketResponse: data || null });
    } catch (error) {
      logger.error('Failed to fetch open ticket details', error, {
        feature: 'reservation-store',
        route: 'fetchOpenTicketDetails',
      });
      set({ openTicketResponse: null });
    }
  },
  fetchRecentZones: async () => {
    try {
      const data = await reservationService.getRecentZoneTickets();
      set({ recentZones: data || [] });
    } catch (error) {
      logger.error('Failed to fetch recent zone tickets', error, {
        feature: 'reservation-store',
        route: 'fetchRecentZones',
      });
    }
  },
  getAllPendingDisclaimertasks: async () => {
    try {
      set({ isLoading: true });
      const tasks = await reservationService.getPendingDisclaimertasks();
      set({ pendingTasks: tasks });
    } catch (error) {
      logger.error('Failed to fetch pending disclaimer tasks', error, {
        feature: 'reservation-store',
        route: 'getAllPendingDisclaimertasks',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  cancelReservation: async (reservationId: string, wholeGroup?: boolean) => {
    try {
      if (wholeGroup === undefined) {
        await reservationService.cancelReservation(reservationId);
      } else {
        await reservationService.cancelReservation(reservationId, wholeGroup);
      }
      await get().getAllReservationsByUser();
      return true;
    } catch (error) {
      logger.error('Failed to cancel reservation', error, {
        feature: 'reservation-store',
        route: 'cancelReservation',
      });
      return false;
    }
  },
  downloadReceipt: async (company: string, reservationId: string) => {
    try {
      const fileData = await reservationService.downloadReceipt(company, reservationId);
      downloadBlob(fileData, `receipt-${reservationId}.pdf`);
    } catch (error) {
      logger.error('Failed to download receipt', error, {
        feature: 'reservation-store',
        route: 'downloadReceipt',
      });
    }
  },
  signContract: async (
    companyId: string,
    taskId: string,
    payload: ReservationContractSignupSaveRequest
  ) => {
    set({ error: null });
    try {
      await reservationService.signContract(companyId, taskId, payload);
      await get().getAllPendingDisclaimertasks();
      return true;
    } catch (error) {
      logger.error('Failed to sign contract', error, {
        feature: 'reservation-store',
        route: 'signContract',
      });
      set({ error: extractApiErrorMessage(error, '') || null });
      return false;
    }
  },
}));
